import type { ReactNode } from 'react';
import { CaretUp } from 'assets/icons';
import CustomLink from 'components/shared/CustomLink';
import { SDD_NDD_BADGE_ENABLED } from 'utils/featureFlags';

type RichTFunction = {
  (key: string): string;
  rich: (
    key: string,
    values: Record<string, (chunks: ReactNode) => ReactNode>,
  ) => ReactNode;
};

/**
 * /ring/faq data. COD is not offered in India, so that answer is dropped
 * there; every other region keeps it.
 */
export const getRingFaqData = (isIndia: boolean, t: RichTFunction) => [
  {
    title: t('ring.faq.data.titleA'),
    description: t('ring.faq.data.contentA'),
  },
  {
    title: t('ring.faq.page.sizes.title'),
    description: t('ring.faq.page.sizes.description'),
  },
  {
    title: t('ring.faq.page.finger.title'),
    description: t.rich('ring.faq.page.finger.description', {
      link: (chunks) => (
        <CustomLink
          href="https://www.notion.so/ultrahumanapp/Ring-sizing-kit-22aa9960c9b649dda983ca21bfa5ac34?pvs=4"
          target={'_blank'}
        >
          {chunks}
        </CustomLink>
      ),
    }),
  },
  {
    title: t('ring.faq.page.sizeMatch.title'),
    description: (
      <>
        {t('ring.faq.page.sizeMatch.paragraphA')}
        <br />
        <br />
        {t('ring.faq.page.sizeMatch.paragraphB')}
      </>
    ),
  },
  {
    title: t('ring.faq.data.titleB'),
    description: t('ring.faq.data.contentB'),
  },
  {
    title: t('ring.faq.data.titleC'),
    description: t('ring.faq.data.contentC'),
  },
  ...(isIndia
    ? []
    : [
        {
          title: t('ring.faq.page.cod.title'),
          description: t('ring.faq.page.cod.description'),
        },
      ]),
  {
    title: t('ring.faq.page.intlGuidelines.title'),
    description: (
      <>
        {t('ring.faq.page.intlGuidelines.paragraphA')}
        <br />
        <br />
        {t('ring.faq.page.intlGuidelines.paragraphB')}
        <br />
        <br />
        {t('ring.faq.page.intlGuidelines.paragraphC')}
      </>
    ),
  },
  {
    title: t('ring.faq.page.ultrahumanX.title'),
    description: (
      <>
        <b>{t('ring.faq.page.ultrahumanX.oneYear.heading')}</b>
        <ul>
          <li>{t('ring.faq.page.ultrahumanX.oneYear.damage')}</li>
          <li>{t('ring.faq.page.ultrahumanX.oneYear.support')}</li>
        </ul>
        <br />
        <b>{t('ring.faq.page.ultrahumanX.twoYear.heading')}</b>
        <ul>
          <li>{t('ring.faq.page.ultrahumanX.twoYear.includes')}</li>
          <li>{t('ring.faq.page.ultrahumanX.twoYear.theft')}</li>
          <li>{t('ring.faq.page.ultrahumanX.twoYear.weightLoss')}</li>
        </ul>
      </>
    ),
  },
  {
    title: t('ring.faq.data.titleE'),
    description: (
      <>
        {t('ring.faq.data.contentE')}
        <b>{t('ring.faq.data.contentEcolorB')}</b>,{' '}
        <b>{t('ring.faq.data.contentEcolorC')}</b>,{' '}
        <b>{t('ring.faq.data.contentEcolorD')}</b>,{' '}
        <b>{t('ring.faq.data.contentEcolorE')}</b> {t('ring.AND')}{' '}
        <b>{t('ring.faq.data.contentEcolorF')}</b>.
      </>
    ),
  },
  {
    title: t('ring.faq.page.batteryLife.title'),
    description: t('ring.faq.page.batteryLife.description'),
  },
  {
    title: t('ring.faq.page.compatibility.title'),
    description: t('ring.faq.page.compatibility.description'),
  },
  {
    title: t('ring.faq.page.material.title'),
    description: t('ring.faq.page.material.description'),
  },
  {
    title: t('ring.faq.page.bodySignals.title'),
    description: t('ring.faq.page.bodySignals.description'),
  },
  {
    title: t('ring.faq.page.hrv.title'),
    description: t('ring.faq.page.hrv.description'),
  },
  {
    title: t('ring.faq.page.appFeatures.title'),
    description: t('ring.faq.page.appFeatures.description'),
  },
  {
    title: t('ring.faq.page.parameters.title'),
    description: t('ring.faq.page.parameters.description'),
  },
  {
    title: t('ring.faq.page.m1Metabolic.title'),
    description: t('ring.faq.page.m1Metabolic.description'),
  },
  {
    title: t('ring.faq.page.m1Sleep.title'),
    description: t('ring.faq.page.m1Sleep.description'),
  },
  {
    title: t('ring.faq.page.vsR1.title'),
    description: (
      <>
        {t('ring.faq.page.vsR1.description')}
        <br />
        <br />
        <i>{t('ring.faq.page.vsR1.footnote')}</i>
      </>
    ),
  },
  {
    title: t('ring.faq.page.vsWearables.title'),
    description: t('ring.faq.page.vsWearables.description'),
  },
  {
    title: t('ring.faq.page.vsM1.title'),
    description: t('ring.faq.page.vsM1.description'),
  },
  {
    title: t('ring.faq.page.accuracy.title'),
    description: t('ring.faq.page.accuracy.description'),
  },
  {
    title: t('ring.faq.page.cgm.title'),
    description: t('ring.faq.page.cgm.description'),
  },
  {
    title: t('ring.faq.page.dailyWear.title'),
    description: t('ring.faq.page.dailyWear.description'),
  },
  {
    title: t('ring.faq.page.workout.title'),
    description: t('ring.faq.page.workout.description'),
  },
  {
    title: t('ring.faq.page.underwater.title'),
    description: t('ring.faq.page.underwater.description'),
  },
  {
    title: t('ring.faq.page.intlGuidelines.title'),
    description: (
      <>
        {t('ring.faq.page.intlGuidelines.paragraphA')}{' '}
        {t('ring.faq.page.intlGuidelines.paragraphB')}{' '}
        {t('ring.faq.page.intlGuidelines.paragraphC')}
      </>
    ),
  },
  {
    title: t('ring.faq.page.calibration.title'),
    description: t('ring.faq.page.calibration.description'),
  },
  {
    title: t('ring.faq.page.chargeTime.title'),
    description: t('ring.faq.page.chargeTime.description'),
  },
  {
    title: t('ring.faq.page.sharing.title'),
    description: t('ring.faq.page.sharing.description'),
  },
  {
    title: t('ring.faq.page.skinEffects.title'),
    description: t('ring.faq.page.skinEffects.description'),
  },
  {
    title: t('ring.faq.page.stuck.title'),
    description: (
      <>
        {t('ring.faq.page.stuck.intro')}
        <br />
        <br />
        {t('ring.faq.page.stuck.tryHeading')}
        <ul>
          <li>{t('ring.faq.page.stuck.tipColdWater')}</li>
          <li>{t('ring.faq.page.stuck.tipSoap')}</li>
          <li>{t('ring.faq.page.stuck.tipTwist')}</li>
          <li>{t('ring.faq.page.stuck.tipRaiseHand')}</li>
          <li>{t('ring.faq.page.stuck.tipWait')}</li>
        </ul>
        <br />
        {t.rich('ring.faq.page.stuck.stillStuck', {
          b: (chunks) => <b>{chunks}</b>,
          strong: (chunks) => <strong>{chunks}</strong>,
        })}
        <ul>
          <li>{t('ring.faq.page.stuck.cutNoDiy')}</li>
          <li>{t('ring.faq.page.stuck.cutBattery')}</li>
          <li>{t('ring.faq.page.stuck.cutWiden')}</li>
          <li>{t('ring.faq.page.stuck.cutAfter')}</li>
        </ul>
      </>
    ),
  },
  {
    title: t('ring.faq.page.emf.title'),
    description: t('ring.faq.page.emf.description'),
  },
  {
    title: t('ring.faq.page.medicalDevice.title'),
    description: (
      <>
        {t('ring.faq.page.medicalDevice.paragraphA')}
        <br />
        <br />
        {t('ring.faq.page.medicalDevice.paragraphB')}
      </>
    ),
  },
  {
    title: t('ring.faq.page.pacemaker.title'),
    description: t('ring.faq.page.pacemaker.description'),
  },
  {
    title: t('ring.faq.page.esd.title'),
    description: t('ring.faq.page.esd.description'),
  },
  {
    title: t('ring.faq.page.tattoo.title'),
    description: t('ring.faq.page.tattoo.description'),
  },
  {
    title: t('ring.faq.page.safetyDesign.title'),
    description: (
      <>
        {t('ring.faq.page.safetyDesign.paragraphA')}
        <br />
        <br />
        {t('ring.faq.page.safetyDesign.paragraphB')}
      </>
    ),
  },
  {
    title: t('ring.faq.page.disposal.title'),
    description: (
      <>
        {t('ring.faq.page.disposal.paragraphA')}
        <br />
        <br />
        {t('ring.faq.page.disposal.paragraphB')}
      </>
    ),
  },
  {
    title: t('ring.faq.page.warranty.title'),
    description: t('ring.faq.page.warranty.description'),
  },
  {
    title: t('ring.faq.page.returnPolicy.title'),
    description: t('ring.faq.page.returnPolicy.description'),
  },
  {
    title: t('ring.faq.page.returnShipping.title'),
    description: t('ring.faq.page.returnShipping.description'),
  },
  {
    title: t('ring.faq.page.cancellation.title'),
    description: t('ring.faq.page.cancellation.description'),
  },
  {
    title: t('ring.faq.page.termsOfSale.title'),
    description: t.rich('ring.faq.page.termsOfSale.description', {
      link: (chunks) => (
        <CustomLink href="/termsOfSale/UltrahumanRing">{chunks}</CustomLink>
      ),
    }),
  },
  {
    title: '',
    description: t('ring.faq.page.colourDisclaimer'),
  },
];

type TFunction = (
  key: string,
  values?: Record<string, string | number>,
) => string;

export const getRingBuyPageFaqData = (t: TFunction) => [
  {
    title: t('ring.faq.data.titleA'),
    description: t('ring.faq.data.contentA'),
  },
  {
    title: t('ring.faq.data.titleB'),
    description: t('ring.faq.data.contentB'),
  },
  {
    title: t('ring.faq.data.titleC'),
    description: t('ring.faq.data.contentC'),
  },
  {
    title: t('ring.faq.data.titleE'),
    description: (
      <>
        {t('ring.faq.data.contentE')}
        <b>{t('ring.faq.data.contentEcolorB')}</b>,{' '}
        <b>{t('ring.faq.data.contentEcolorC')}</b>,{' '}
        <b>{t('ring.faq.data.contentEcolorD')}</b>,{' '}
        <b>{t('ring.faq.data.contentEcolorE')}</b> {t('ring.AND')}{' '}
        <b>{t('ring.faq.data.contentEcolorF')}</b>.
      </>
    ),
  },
  {
    title: t('ring.faq.data.tradeInReturn.title'),
    description: t('ring.faq.data.tradeInReturn.description'),
  },
  {
    title: t('ring.faq.data.tradeInRecycle.title'),
    description: t('ring.faq.data.tradeInRecycle.description'),
  },
  {
    title: '',
    description: (
      <>
        <CustomLink href="/ring/faq">
          {t('ring.faq.seeAll')}{' '}
          <CaretUp
            style={{ transform: 'rotate(90deg)' }}
            width={14}
            height={14}
          />
        </CustomLink>
      </>
    ),
  },
];

/** AE buy FAQ is the default set with COD inserted after colours. */
export const getRingBuyPageFaqData_AE = (t: TFunction) => {
  const items = getRingBuyPageFaqData(t);
  const afterColours =
    items.findIndex((item) => item.title === t('ring.faq.data.titleE')) + 1;
  return [
    ...items.slice(0, afterColours),
    {
      title: t('ring.faq.page.cod.title'),
      description: t('ring.faq.page.cod.description'),
    },
    ...items.slice(afterColours),
  ];
};

/** Ring Pro buy page FAQ: no Brushed Rose Gold question, four colours only, then a See all FAQs link to /ring-pro/faq. */
export const getRingProBuyPageFaqData = (t: TFunction) => [
  {
    title: t('ring-pro:ring-pro.faq.data.titleA'),
    description: t('ring-pro:ring-pro.faq.data.contentA'),
  },
  {
    title: t('ring-pro:ring-pro.faq.data.titleB'),
    description: t('ring-pro:ring-pro.faq.data.contentB'),
  },
  {
    title: t('ring-pro:ring-pro.faq.data.titleC'),
    description: t('ring-pro:ring-pro.faq.data.contentC'),
  },
  {
    title: t('ring-pro:ring-pro.faq.data.titleD'),
    description: (
      <>
        {t('ring-pro:ring-pro.faq.data.contentD')}
        <b>{t('ring-pro:ring-pro.faq.data.contentDcolorA')}</b>,{' '}
        <b>{t('ring-pro:ring-pro.faq.data.contentDcolorB')}</b>,{' '}
        <b>{t('ring-pro:ring-pro.faq.data.contentDcolorC')}</b>{' '}
        {t('ring-pro:ring-pro.faq.data.and')}{' '}
        <b>{t('ring-pro:ring-pro.faq.data.contentDcolorD')}</b>.
      </>
    ),
  },
  {
    title: t('ring-pro:ring-pro.faq.data.titleE'),
    description: t('ring-pro:ring-pro.faq.data.contentE'),
  },
  {
    title: t('ring-pro:ring-pro.faq.data.titleF'),
    description: t('ring-pro:ring-pro.faq.data.contentF'),
  },
  {
    title: '',
    description: (
      <>
        <CustomLink href="/ring-pro/faq">
          {t('ring:ring.faq.seeAll')}{' '}
          <CaretUp
            aria-hidden
            style={{ transform: 'rotate(90deg)' }}
            width={14}
            height={14}
          />
        </CustomLink>
      </>
    ),
  },
];

/**
 * India ring buy page FAQ. 2-hour delivery Qs are gated by SDD_NDD_BADGE_ENABLED;
 * when off, the shared "what's next" answer sits after sizing.
 */
export const getRingBuyPageFaqData_IN = (t: TFunction) => [
  ...(SDD_NDD_BADGE_ENABLED
    ? [
        {
          title: t('ring.faq.data.in.cities.title'),
          description: t('ring.faq.data.in.cities.content'),
        },
        {
          title: t('ring.faq.data.in.cutoff.title'),
          description: t('ring.faq.data.in.cutoff.content'),
        },
        {
          title: t('ring.faq.data.in.missed.title'),
          description: t('ring.faq.data.in.missed.content'),
        },
        {
          title: t('ring.faq.data.in.when.title'),
          description: t('ring.faq.data.in.when.content'),
        },
      ]
    : []),
  {
    title: t('ring.faq.data.titleA'),
    description: t('ring.faq.data.contentA'),
  },
  ...(SDD_NDD_BADGE_ENABLED
    ? []
    : [
        {
          title: t('ring.faq.data.titleB'),
          description: t('ring.faq.data.contentB'),
        },
      ]),
  {
    title: t('ring.faq.data.titleC'),
    description: t('ring.faq.data.contentC'),
  },
  {
    title: t('ring.faq.data.titleE'),
    description: (
      <>
        {t('ring.faq.data.contentE')}
        <b>{t('ring.faq.data.contentEcolorB')}</b>,{' '}
        <b>{t('ring.faq.data.contentEcolorC')}</b>,{' '}
        <b>{t('ring.faq.data.contentEcolorD')}</b>,{' '}
        <b>{t('ring.faq.data.contentEcolorE')}</b> {t('ring.AND')}{' '}
        <b>{t('ring.faq.data.contentEcolorF')}</b>.
      </>
    ),
  },
  {
    title: t('ring.faq.data.tradeInReturn.title'),
    description: t('ring.faq.data.tradeInReturn.description'),
  },
  {
    title: t('ring.faq.data.tradeInRecycle.title'),
    description: t('ring.faq.data.tradeInRecycle.description'),
  },
  {
    title: '',
    description: (
      <>
        <CustomLink href="/ring/faq">
          {t('ring.faq.seeAll')}{' '}
          <CaretUp
            style={{ transform: 'rotate(90deg)' }}
            width={14}
            height={14}
          />
        </CustomLink>
      </>
    ),
  },
];
